/**
 * Landings SEO por municipio y categoría (`/mataro/piscinas`, `/barcelona/piscinas-con-barbacoa`):
 * copy, rutas y enlaces.
 *
 * Las categorías, los espacios y su copy salen de `src/config/seoLandings.json`. Qué municipios
 * y qué páginas existen, y qué anuncios, reseñas, precios y municipios cercanos lleva cada una,
 * lo calcula un job diario en el servidor (server/seoLandings/) a partir de los anuncios; la web
 * solo lo pinta. Ver docs/seo-landings.md.
 */

import seoLandingsConfig from '../config/seoLandings.json';
import { types as sdkTypes } from './sdkLoader';
import { routeForSearchTab } from './searchListingTabs';

/** Tarjetas que enseña cada página como mucho ("Piscinas por horas en Barcelona"). */
export const SEO_LANDING_TOP_COUNT = 12;

const interpolate = (template, values) =>
  String(template || '')
    .replace(/\{(\w+)\}/g, (match, key) => (values[key] != null ? values[key] : match))
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.:])/g, '$1')
    .trim();

const capitalize = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const { LatLng, LatLngBounds } = sdkTypes;

/**
 * Caja del municipio que calculó el job (`{ address, bounds: { ne, sw }, origin }`, pares
 * `[lat, lng]`) → tipos del SDK, para el buscador del hero y "Ver más".
 */
const toLocation = place => {
  const { address, bounds, origin } = place || {};
  if (!bounds || !origin) {
    return null;
  }
  return {
    address: address || place.name,
    bounds: new LatLngBounds(new LatLng(...bounds.ne), new LatLng(...bounds.sw)),
    origin: new LatLng(...origin),
  };
};

/**
 * Nombre provisional a partir del slug ("sant-cugat-del-valles" → "Sant cugat del valles"),
 * mientras la página carga en el cliente. En SSR la página ya trae el nombre de verdad.
 */
export const placeholderSeoCity = slug => ({
  slug,
  name: capitalize(String(slug || '').replace(/-/g, ' ')),
  location: null,
});

/**
 * Página resuelta para `/:city/:category`, o `null` si la categoría no existe. Que el
 * municipio tenga esa página (2 anuncios o más) lo decide el job, no esto.
 *
 * @param {{ slug, name, location }} city el municipio, como lo guardó el job
 */
export const seoLandingFor = (city, pageSlug, config = seoLandingsConfig) => {
  const page = config.pages.find(p => p.slug === pageSlug);
  if (!city?.slug || !page) {
    return null;
  }
  const space = config.spaces[page.space];
  // `copy` de la página manda sobre el de la categoría ("Alquiler de jardines privados…").
  const category = config.categories[page.category] && {
    ...config.categories[page.category],
    ...page.copy,
  };
  if (!space || !category) {
    return null;
  }

  // Sin `count`: el titular del bloque lo pone al final, cuando se sabe cuántas hay.
  const copyValues = { ...space, qualifier: category.qualifier || '', city: city.name };
  const copy = key => interpolate(category[key] || config.templates[key], copyValues);

  return {
    citySlug: city.slug,
    cityName: city.name,
    hasCityImage: !!city.hasImage,
    slug: page.slug,
    spaceKey: page.space,
    spaceNoun: space.Noun,
    categoryKey: page.category,
    qualifier: category.qualifier || '',
    // Texto corto del enlace interno, que ya va bajo el título del espacio y del municipio.
    linkLabel: category.linkLabel || capitalize(category.qualifier),
    location: toLocation(city.location),
    searchTab: space.searchTab,
    faceParam: space.faceParam || null,
    filter: category.filter || {},
    title: copy('title'),
    h1: copy('h1'),
    description: copy('description'),
    intro: copy('intro'),
    topHeadingTemplate: copy('topHeading'),
    nearbyTitle: copy('nearbyTitle'),
    // Preguntas propias de la página, delante de las comunes.
    faqItems: category.faqItems || [],
  };
};

/** Titular del bloque de tarjetas ("Piscinas con barbacoa por horas en Barcelona"). */
export const seoLandingTopHeading = landing => landing.topHeadingTemplate;

/**
 * Path de la ruta: cualquier municipio, pero solo categorías de la config. Cualquier otro
 * `/x/y` sigue cayendo en el 404; un municipio sin la página, en el 404 de la propia página.
 */
export const seoLandingRoutePath = (config = seoLandingsConfig) =>
  `/:city/:category(${config.pages.map(p => p.slug).join('|')})`;

/** Otras páginas que existen en el mismo municipio, para el enlazado interno. */
export const siblingSeoLandings = (landing, availablePages, config = seoLandingsConfig) => {
  const city = { slug: landing.citySlug, name: landing.cityName };
  return config.pages
    .filter(p => p.slug !== landing.slug && availablePages.includes(p.slug))
    .map(p => seoLandingFor(city, p.slug, config))
    .filter(Boolean);
};

/**
 * Landings agrupadas por espacio (Piscinas / Jardines / Espacios / Espacios interiores), en el
 * orden de la config,
 * para el bloque de enlaces internos. Los grupos vacíos no aparecen.
 */
export const groupSeoLandingsBySpace = (landings, config = seoLandingsConfig) =>
  Object.keys(config.spaces)
    .map(spaceKey => ({
      spaceKey,
      title: config.spaces[spaceKey].Noun,
      landings: landings.filter(l => l.spaceKey === spaceKey),
    }))
    .filter(group => group.landings.length > 0);

// ================ Imagen ================ //

/**
 * Medidas de la imagen de cada landing (foto del municipio + capa de la página). Las da la capa:
 * el servidor la escala a 1200 de ancho con su proporción, y las de diseño son 16:9.
 */
export const SEO_LANDING_IMAGE_SIZE = { width: 1200, height: 675 };

/**
 * ¿Hay imágenes de landing? Las sirve el servidor (`/din-images/…`, server/seoLandings/images.js)
 * con las fuentes que el equipo sube al bucket; hasta que estén, la landing no pinta ninguna.
 */
export const seoLandingImagesEnabled = () => process.env.REACT_APP_SEO_LANDING_IMAGES === 'true';

/**
 * `/din-images/barcelona-piscinas-con-barbacoa.jpg` si el municipio tiene foto propia;
 * `/din-images/piscinas-con-barbacoa.jpg` si no (la genérica, la misma para todos).
 */
export const seoLandingImagePath = landing =>
  landing.hasCityImage
    ? `/din-images/${landing.citySlug}-${landing.slug}.jpg`
    : `/din-images/${landing.slug}.jpg`;

/**
 * `src` de la imagen: en desarrollo la sirve el servidor de la API, en otro puerto; en
 * producción, el mismo dominio.
 */
export const seoLandingImageSrc = landing => {
  const devPort = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const devBase =
    process.env.NODE_ENV === 'development' && devPort ? `http://localhost:${devPort}` : '';
  return `${devBase}${seoLandingImagePath(landing)}`;
};

// ================ Canonical de la búsqueda ================ //

/**
 * Slug de un municipio ("L'Ametlla del Vallès" → `l-ametlla-del-valles`). Misma regla que
 * `municipalitySlug` en server/seoLandings/rules.js, con la que el job nombra las páginas; un test
 * comprueba que coinciden.
 */
export const municipalitySlug = raw =>
  String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Municipio y, si la trae, categoría de una búsqueda, a partir de la primera parte de `address`
 * ("Mataró, Barcelona, Spain"). Los enlaces de /p/sitemap (Console) llevan el tema en la propia
 * dirección ("Barcelona para fiestas y eventos"): si acaba en el `searchSuffix` de una categoría,
 * el resto es el municipio y la coletilla decide la categoría.
 */
const searchAddressParts = (address, config = seoLandingsConfig) => {
  const slug = municipalitySlug(String(address || '').split(',')[0]);
  const suffixes = Object.entries(config.categories)
    .filter(([, c]) => c.searchSuffix)
    .map(([categoryKey, c]) => ({ categoryKey, suffix: `-${municipalitySlug(c.searchSuffix)}` }))
    .filter(({ suffix }) => slug.endsWith(suffix) && slug.length > suffix.length)
    .sort((a, b) => b.suffix.length - a.suffix.length);
  if (suffixes.length === 0) {
    return { citySlug: slug, suffixCategories: [] };
  }
  const { suffix } = suffixes[0];
  return {
    citySlug: slug.slice(0, -suffix.length),
    // Varias categorías pueden compartir coletilla (fiestas / fiestas y eventos, según el espacio).
    suffixCategories: suffixes.filter(c => c.suffix === suffix).map(c => c.categoryKey),
  };
};

/** Municipio de una búsqueda (`address`), sin la coletilla de tema si la lleva. */
export const seoCityOfAddress = (address, config = seoLandingsConfig) =>
  searchAddressParts(address, config).citySlug;

// Pestaña de la búsqueda (param `tab` de la ruta) → espacio de las landings. Interiores no tiene.
const SPACE_FOR_SEARCH_TAB = {
  pool: 'piscinas',
  jardines: 'jardines',
  garden: 'jardines',
  interiores: 'interiores',
  todos: 'espacios',
};

// Filtro de la categoría ↔ param de la búsqueda.
const FILTER_PARAMS = { extras: 'pub_extras', regulation: 'pub_Regulation' };

/**
 * `regulation` es una lista de valores (basta uno: `Pets,pets`) o una lista de grupos (uno de
 * cada grupo: celebraciones **y** música). Igual que `regulationGroups` en server/seoLandings/rules.js.
 */
const regulationGroups = regulation => (Array.isArray(regulation[0]) ? regulation : [regulation]);

const paramValues = raw =>
  String(raw || '')
    .replace(/^has_(any|all):/, '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);

/**
 * Categoría que expresa una búsqueda con sus filtros, o `null` si no lleva filtro de categoría,
 * `undefined` si lleva alguno que ninguna categoría expresa sola (dos filtros, o uno sin página).
 * Un filtro casa con una categoría si sus valores están entre los de la categoría (`Pets` casa
 * con `Pets,pets`); si casa con varias, gana la que tiene exactamente esos valores, y si no, la
 * más estrecha.
 */
const categoryOfSearch = (queryParams, spaceKey, config) => {
  const used = [
    ...Object.entries(FILTER_PARAMS)
      .filter(([, param]) => queryParams[param])
      .map(([key, param]) => ({ key, values: paramValues(queryParams[param]) })),
    ...(queryParams.pub_poolMaintenance
      ? [{ key: 'poolMaintenance', values: [String(queryParams.pub_poolMaintenance)] }]
      : []),
  ];
  if (used.length === 0) {
    return null;
  }
  if (used.length > 1) {
    return undefined;
  }
  const [{ key, values }] = used;
  const candidates = config.pages
    .filter(p => p.space === spaceKey)
    .map(p => ({ slug: p.slug, filter: config.categories[p.category]?.filter || {} }))
    .filter(({ filter }) => Object.keys(filter).length === 1 && filter[key] != null)
    .map(({ slug, filter }) => {
      const groups = regulationGroups([].concat(filter[key]));
      return { slug, groups, allowed: [].concat(...groups) };
    })
    // Todos los valores de la búsqueda son de la categoría, y cubre cada uno de sus grupos.
    .filter(
      ({ groups, allowed }) =>
        values.every(v => allowed.includes(v)) && groups.every(g => g.some(v => values.includes(v)))
    )
    .sort((a, b) => a.allowed.length - b.allowed.length);
  const exact = candidates.find(c => c.allowed.length === values.length);
  return (exact || candidates[0])?.slug;
};

// Primer número de cada tramo de personas (`31-50`, `has_any:21-25,26-30`, `51_above`).
const capacityLowerBounds = raw =>
  paramValues(raw)
    .map(v => parseInt(v, 10))
    .filter(n => Number.isFinite(n));

/**
 * Categoría por número de personas, solo si la búsqueda no lleva otro filtro: la de mayor
 * `minCapacity` que cumplen todos los tramos pedidos, si es la única con ese mínimo (21+ →
 * grupos grandes; 16-20 no decide, porque despedidas y empresa piden los dos 16).
 */
const categoryOfCapacity = (queryParams, spaceKey, config) => {
  const bounds = capacityLowerBounds(queryParams.pub_capacity);
  if (bounds.length === 0) {
    return null;
  }
  const lowest = Math.min(...bounds);
  const candidates = config.pages
    .filter(p => p.space === spaceKey)
    .map(p => ({ slug: p.slug, min: config.categories[p.category]?.filter?.minCapacity }))
    .filter(({ min }) => min != null && min <= lowest);
  const top = Math.max(...candidates.map(c => c.min));
  const best = candidates.filter(c => c.min === top);
  return best.length === 1 ? best[0].slug : null;
};

/**
 * Landing que hace de canonical de una búsqueda (`/s?address=Barcelona…&pub_Regulation=…` →
 * `/barcelona/piscinas-pet-friendly`): la de la categoría si existe; si no (o la búsqueda mezcla
 * filtros), la genérica del espacio; si tampoco, `null` y la búsqueda se queda con el suyo.
 * La coletilla de `address` ("Barcelona para grupos grandes") manda sobre los filtros; las
 * personas solo cuentan sin otro filtro (ver `categoryOfCapacity`). Fechas, horas, orden, página
 * y caja no cuentan: no cambian de qué trata la página.
 *
 * @param {Object} search `{ tab, queryParams }` de la búsqueda
 * @param {Array<string>} availablePages páginas que el job creó para ese municipio
 * @returns {string|null} path, p. ej. `/barcelona/piscinas`
 */
export const seoLandingCanonicalPath = (
  { tab, queryParams = {} },
  availablePages,
  config = seoLandingsConfig
) => {
  const { citySlug, suffixCategories } = searchAddressParts(queryParams.address, config);
  const spaceKey = SPACE_FOR_SEARCH_TAB[tab || 'pool'];
  if (!citySlug || !spaceKey || !availablePages?.length) {
    return null;
  }
  const bySuffix = suffixCategories.length
    ? config.pages.find(p => p.space === spaceKey && suffixCategories.includes(p.category))?.slug
    : undefined;
  const byFilters = suffixCategories.length
    ? undefined
    : categoryOfSearch(queryParams, spaceKey, config);
  const category =
    bySuffix ||
    (byFilters === null ? categoryOfCapacity(queryParams, spaceKey, config) : byFilters);
  const generic = config.pages.find(
    p => p.space === spaceKey && p.category === 'general' && availablePages.includes(p.slug)
  );
  const slug = category && availablePages.includes(category) ? category : generic?.slug;
  return slug ? `/${citySlug}/${slug}` : null;
};

/**
 * Preguntas frecuentes de una página, ya con la ciudad (o el municipio) y el espacio. La del
 * precio usa las cifras de la página que calculó el job (`priceStats`, de los anuncios cercanos)
 * si hay al menos dos con precio; si no, una respuesta genérica. La reserva de ejemplo y la media
 * de las más reservadas se añaden solo si hay dato.
 *
 * @param {Object} landing de `seoLandingFor`
 * @param {{ min, count, listingCount, plan } | null} priceStats subunidades de euro
 * @param {Function} formatPrice subunidades → texto ("13,20 €", "300 €")
 * @returns {{ title: string, items: Array<{ id, q, a, link? }>, more: Object }}
 */
export const seoLandingFaq = (landing, priceStats, formatPrice, config = seoLandingsConfig) => {
  const space = config.spaces[landing.spaceKey] || {};
  const values = { ...space, qualifier: landing.qualifier || '', city: landing.cityName };
  const withPrices = priceStats && priceStats.count >= 2;
  const plan = withPrices ? priceStats.plan : null;
  const priceValues = withPrices
    ? {
        count: priceStats.count,
        listingCount: priceStats.listingCount || priceStats.count,
        min: formatPrice(priceStats.min),
        median: formatPrice(priceStats.median),
        ...(plan
          ? {
              planGuests: plan.guests,
              planHours: plan.hours,
              planMin: formatPrice(plan.min),
              planPerPerson: formatPrice(Math.round(plan.min / plan.guests)),
              ...(plan.topBookedAverage ? { planAverage: formatPrice(plan.topBookedAverage) } : {}),
            }
          : {}),
      }
    : {};
  // La respuesta con cifras, por frases: cada una solo si tiene sus datos.
  const answerOf = item => {
    if (!withPrices || !item.aWithPrices) {
      return item.a;
    }
    return [
      item.aWithPrices,
      plan && item.aPlan,
      plan && plan.topBookedAverage && item.aPlanAverage,
    ]
      .filter(Boolean)
      .join(' ');
  };
  return {
    title: interpolate(config.faq.title, values),
    more: config.faq.more,
    items: [...(landing.faqItems || []), ...config.faq.items].map(item => ({
      id: item.id,
      q: interpolate(item.q, values),
      a: interpolate(answerOf(item), {
        ...values,
        ...priceValues,
      }),
      link: item.link || null,
    })),
  };
};

const hasAny = values => `has_any:${values.join(',')}`;

// Con grupos, Flex no sabe pedir "uno de cada": `has_all` con el primero de cada grupo.
const regulationParam = regulation => {
  const groups = regulationGroups(regulation);
  return groups.length > 1 ? `has_all:${groups.map(g => g[0]).join(',')}` : hasAny(groups[0]);
};

/**
 * Parámetros de búsqueda del filtro de la categoría, para el buscador del hero y "Ver más".
 * La capacidad no tiene: el buscador la filtra por buckets y daría otra cosa.
 */
export const seoLandingFlexParams = filter => {
  const { extras, regulation, poolMaintenance } = filter || {};
  return {
    ...(extras?.length ? { pub_extras: hasAny(extras) } : {}),
    ...(regulation?.length ? { pub_Regulation: regulationParam(regulation) } : {}),
    ...(poolMaintenance ? { pub_poolMaintenance: poolMaintenance } : {}),
  };
};

/**
 * Enlace "Ver todos" al buscador: misma ciudad, misma pestaña y los filtros que el
 * buscador entiende como chips/filtros (`pub_extras`, `pub_Regulation`). La capacidad y el
 * tipo de agua no viajan: el buscador no los filtra igual y daría resultados distintos.
 */
export const seoLandingSearchLink = landing => {
  const { routeName, params } = routeForSearchTab(landing.searchTab);
  const { address, bounds, origin } = landing.location || { address: landing.cityName };
  const { pub_extras, pub_Regulation } = seoLandingFlexParams(landing.filter);
  return {
    routeName,
    params,
    searchParams: {
      address,
      bounds,
      origin,
      ...(pub_extras ? { pub_extras } : {}),
      ...(pub_Regulation ? { pub_Regulation } : {}),
    },
  };
};

/** Valores iniciales del buscador del hero: el municipio y el tipo de espacio de la página. */
export const seoLandingHeroInitialValues = landing => {
  const { address, bounds, origin } = landing.location || {};
  const LISTING_TYPE_FOR_TAB = { pool: 'pool', jardines: 'garden', interiores: 'interiores' };
  const listingType = LISTING_TYPE_FOR_TAB[landing.searchTab] || null;
  return { address, bounds, origin, listingType };
};

// ================ Miga de pan de la ficha ================ //

/**
 * Miga de pan de la ficha de un anuncio ("Cocopool.es › Piscinas en Mataró"): la landing general
 * del municipio del anuncio para el espacio que se mira (`interiores`, `jardines` o `piscinas`)
 * o, si el municipio no la tiene, la de piscinas. Sin ninguna de las dos, el texto del espacio
 * que se mira sin enlace (`slug` y `path` a `null`); `null` solo si el anuncio no tiene municipio.
 *
 * @param {Object} params
 * @param {string} params.cityName `publicData.city` del anuncio
 * @param {string} params.spaceKey espacio de la cara que se mira
 * @param {Array<string>} params.availablePages páginas que el job creó en el municipio
 * @returns {{ citySlug, slug: ?string, path: ?string, label } | null}
 */
export const seoListingBreadcrumb = (
  { cityName, spaceKey, availablePages },
  config = seoLandingsConfig
) => {
  const citySlug = municipalitySlug(cityName);
  const generalOf = key => config.pages.find(p => p.space === key && p.category === 'general');
  const page = [spaceKey, 'piscinas']
    .map(generalOf)
    .find(p => p && (availablePages || []).includes(p.slug));
  if (!citySlug) {
    return null;
  }
  const label = space => `${config.spaces[space].Noun} en ${String(cityName).trim()}`;
  if (!page) {
    return { citySlug, slug: null, path: null, label: label(spaceKey) };
  }
  return {
    citySlug,
    slug: page.slug,
    path: `/${citySlug}/${page.slug}`,
    label: label(page.space),
  };
};
